// Getting the keyboard up for a text field on a screen that's about to appear.
//
// iOS web views (and Android's, less strictly) only raise the keyboard when
// focus happens DURING a user's tap. A reply action opens a thread screen whose
// reply box doesn't exist yet at tap time, and focusing it a frame later is too
// late: the box gets focus but no keyboard shows, so you tap again.
//
// The standard fix: focus a tiny invisible input synchronously inside the tap,
// so the keyboard comes up, then move focus to the real field when it mounts;
// moving focus between fields keeps the keyboard up. If nothing takes focus
// within a moment (the thread was already showing, say), the primer lets go so
// the keyboard doesn't linger with nowhere to type.

let primer: HTMLInputElement | null = null
let releaseTimer: ReturnType<typeof setTimeout> | null = null

const RELEASE_AFTER_MS = 1000

export function primeKeyboard(): void {
  if (typeof document === 'undefined') return
  if (!primer || !primer.isConnected) {
    primer = document.createElement('input')
    primer.type = 'text'
    primer.setAttribute('aria-hidden', 'true')
    primer.tabIndex = -1
    primer.className = 'keyboard-primer'
    // 16px: smaller type makes iOS zoom the page on focus.
    Object.assign(primer.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      border: '0',
      padding: '0',
      fontSize: '16px',
      pointerEvents: 'none',
    })
    document.body.appendChild(primer)
  }
  primer.focus({ preventScroll: true })
  if (releaseTimer) clearTimeout(releaseTimer)
  releaseTimer = setTimeout(() => {
    releaseTimer = null
    if (primer && document.activeElement === primer) primer.blur()
  }, RELEASE_AFTER_MS)
}

export function isKeyboardPrimer(el: Element | null): boolean {
  return el !== null && el === primer
}
